/**
 * PhasePowerRecommendationBlock — the per-phase power recommendation surface
 * inside the phase card (Project Intent form).
 *
 * Render discipline (mirrors the max-power panel's honesty rules):
 *   - renders NOTHING when no plan is threaded (wizard/rerun mounts) or the
 *     phase has no entry;
 *   - 'not-recommendable' / 'conflict' → ONE muted reason chip, never a hole;
 *   - undocumented availability rows are SHOWN, never hidden;
 *   - Apply is a two-step confirm routed through the host's existing intent
 *     draft flow — no new write path; the shortfall banner never softens.
 */
import React, { useState } from 'react';
import type { PhasePowerComponentKey } from '../../types/siteRag';
import { PHASE_POWER_COMPONENT_LABEL } from '../../types/siteRag';
import type {
  PhasePlanEntry,
  PhasePlanPerSource,
} from '../../lib/powerAvailability/recommendPhasePowerPlan';

const REASON_COPY: Record<string, string> = {
  'load-range-undecided': 'Phase mix sizing awaits a declared capacity (exact or range); screening can recommend options first.',
  'or-mode-no-combined-claim':
    'Phase mix sizing is unavailable for alternatives (Either / Or). A combined mix uses Combined (And).',
  'no-components': 'Phase mix sizing awaits selected sources; screening recommendations do not require this selection.',
  'floors-exceed-availability':
    'Conflict: declared per-source minimums exceed documented availability — reduce the floors or document more capacity.',
  'range-conflicts-envelope':
    'Conflict: the phase capacity range sits above the site max-power envelope.',
  'range-conflicts-prior-phase':
    'Conflict: the phase capacity range sits below the prior phase (cumulative capacity can’t shrink).',
};

const BINDER_LABEL: Record<string, string> = {
  'user-range': 'bound by declared range',
  'grid-availability': 'bound by grid availability',
  'gas-availability': 'bound by gas availability',
  'site-envelope': 'bound by site max envelope',
  'goal-range': 'bound by site goal range',
  'prior-phase-floor': 'floored by prior phase',
};

function availabilityChip(s: PhasePlanPerSource): { text: string; cls: string } {
  switch (s.availabilityStatus) {
    case 'documented':
      return { text: 'availability documented', cls: 'text-primary' };
    case 'proxy':
      return { text: 'voltage-class proxy — not ATC', cls: 'text-amber-500' };
    case 'not-applicable':
      return { text: 'storage — user range only', cls: 'text-text-muted' };
    default:
      return { text: 'availability not documented — user range only', cls: 'text-amber-500' };
  }
}

function boundText(s: PhasePlanPerSource): string {
  const floor =
    s.floorWhy === 'none' ? null : `min ${s.floorMW} (${s.floorWhy === 'user-exact' ? 'declared exact' : s.floorWhy === 'user-min' ? 'declared min' : 'prior phase'})`;
  const cap =
    s.ceilingMW == null
      ? null
      : `cap ${s.ceilingMW} (${
          s.ceilingWhy === 'gas-pool' ? 'gas pool'
            : s.ceilingWhy === 'grid-availability' ? 'grid availability'
              : s.ceilingWhy === 'user-exact' ? 'declared exact'
                : s.ceilingWhy === 'no-growth-undocumented' ? 'no growth — undocumented'
                  : 'declared max'
        })`;
  return [floor, cap].filter(Boolean).join(' · ');
}

export const PhasePowerRecommendationBlock: React.FC<{
  entry: PhasePlanEntry | null | undefined;
  /** Non-null when an APPLIED plan is stale (shown on the applied tag). */
  staleReason?: string | null;
  /** True when this phase's figures were applied from the plan. */
  applied?: boolean;
  onApply?: (phaseId: string) => void;
  testIdPrefix: string;
}> = ({ entry, staleReason, applied, onApply, testIdPrefix }) => {
  const [confirming, setConfirming] = useState(false);
  if (!entry) return null;
  const tid = `${testIdPrefix}-power-plan`;

  if (entry.status !== 'recommended') {
    const reason = entry.reasons[0];
    return (
      <div
        className="type-meta text-text-muted normal-case tracking-normal"
        data-testid={`${tid}-unavailable`}
      >
        {(reason && REASON_COPY[reason]) ?? 'Phase mix sizing is unavailable.'}
      </div>
    );
  }

  const range = entry.loadRange;
  const rangeLabel = range
    ? Number.isFinite(range.max)
      ? range.min === range.max
        ? `declared ${range.min} MW`
        : `${range.min}–${range.max} MW range`
      : `≥ ${range.min} MW range`
    : null;

  return (
    <div
      className="space-y-1.5 border border-border-subtle bg-canvas rounded-md p-2"
      data-testid={tid}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-meta text-text-secondary">Recommended power plan</span>
        <span className="text-[12px] text-text-primary normal-case tracking-normal">
          {entry.recommendedLoadMW} MW
          {rangeLabel ? ` of the ${rangeLabel}` : ''}
        </span>
        {entry.binders.map(b => (
          <span
            key={b}
            className="text-[10px] text-text-muted border border-border-subtle rounded-sm px-1.5 py-0.5 normal-case tracking-normal"
            data-testid={`${tid}-binder-${b}`}
          >
            {BINDER_LABEL[b] ?? b}
          </span>
        ))}
        {applied && (
          <span
            className="text-[10px] border border-primary/40 text-primary rounded-sm px-1.5 py-0.5 normal-case tracking-normal"
            data-testid={`${tid}-applied`}
          >
            Applied
          </span>
        )}
        {applied && staleReason && (
          <span
            className="text-[10px] text-amber-500 normal-case tracking-normal"
            data-testid={`${tid}-stale`}
          >
            {staleReason}
          </span>
        )}
      </div>

      {entry.shortfall && (
        <div
          role="alert"
          className="text-[11px] text-danger normal-case tracking-normal"
          data-testid={`${tid}-shortfall`}
        >
          Documented supply serves {entry.recommendedLoadMW} MW — {entry.shortfall.belowPhaseMinMW} MW
          short of this phase&apos;s declared minimum. The gap is real; nothing here closes it on paper.
        </div>
      )}

      <div className="space-y-0.5">
        {entry.perSource.map(s => {
          const chip = availabilityChip(s);
          return (
            <div
              key={s.key}
              className="flex flex-wrap items-center gap-2 text-[11px] normal-case tracking-normal"
              data-testid={`${tid}-row-${s.key}`}
            >
              <span className="w-28 text-text-secondary">
                {PHASE_POWER_COMPONENT_LABEL[s.key as PhasePowerComponentKey]}
              </span>
              <span className="text-text-primary">{s.recommendedMW} MW</span>
              <span className="text-text-muted">{boundText(s)}</span>
              <span className={chip.cls}>{chip.text}</span>
            </div>
          );
        })}
      </div>

      {entry.adequacy && (
        <div
          className="text-[10px] text-text-muted normal-case tracking-normal"
          data-testid={`${tid}-adequacy`}
          data-verdict={entry.adequacy.verdict}
        >
          {entry.adequacy.message}
        </div>
      )}

      {entry.disclosures.length > 0 && (
        <ul className="type-meta text-text-muted normal-case tracking-normal list-disc pl-4">
          {entry.disclosures.map(d => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}

      {onApply && (
        <div className="flex items-center gap-2">
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="text-[11px] border border-border-subtle rounded-md px-2 py-0.5 text-text-secondary hover:border-primary hover:text-primary normal-case tracking-normal"
              data-testid={`${tid}-apply`}
            >
              Apply to this phase
            </button>
          ) : (
            <>
              <span className="text-[11px] text-text-secondary normal-case tracking-normal">
                Writes {entry.perSource.length} source MW value{entry.perSource.length === 1 ? '' : 's'} into this
                phase&apos;s mix (saved with the form).
              </span>
              <button
                type="button"
                onClick={() => {
                  setConfirming(false);
                  onApply(entry.phaseId);
                }}
                className="text-[11px] border border-primary rounded-md px-2 py-0.5 text-primary normal-case tracking-normal"
                data-testid={`${tid}-confirm`}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="text-[11px] border border-border-subtle rounded-md px-2 py-0.5 text-text-muted normal-case tracking-normal"
                data-testid={`${tid}-cancel`}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
